import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import layoutData from "../data/private/layout.json";
import flowGuidanceData from "../data/private/flowGuidance.json";
import VastuCompassPlaceholder from "../components/VastuCompassPlaceholder";
import { logActivity } from "../utils/activity";
import normalizeApiError from "../utils/normalizeApiError";
import FlowGuidanceBanner from "../components/FlowGuidanceBanner";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import { getWorkspaceLayouts } from "../api/workspace.api";

export default function DashboardLayoutSetup() {
  const navigate = useNavigate();
  const [layouts, setLayouts] = useState([]);
  const [activeLayoutIndex, setActiveLayoutIndex] = useState(null);
  const [loadingLayouts, setLoadingLayouts] = useState(true);
  const [layoutsError, setLayoutsError] = useState(null);
  const selectedClientId = localStorage.getItem("myv_selected_client_id") || "";
  const selectedPropertyId =
    localStorage.getItem("myv_selected_property_id") || "";

  useEffect(() => {
    const fetchLayouts = async () => {
      if (!selectedPropertyId) {
        setLayouts([]);
        setActiveLayoutIndex(null);
        setLayoutsError(null);
        setLoadingLayouts(false);
        return;
      }
      try {
        const data = await getWorkspaceLayouts();
        const nextLayouts = Array.isArray(data) ? data : [];
        setLayouts(nextLayouts);
        setActiveLayoutIndex(nextLayouts.length ? 0 : null);
        setLayoutsError(null);
      } catch (error) {
        setLayouts([]);
        setActiveLayoutIndex(null);
        setLayoutsError(normalizeApiError(error));
        logActivity("api_error", { module: "layout" });
      } finally {
        setLoadingLayouts(false);
      }
    };
    fetchLayouts();
  }, [selectedPropertyId]);

  if (!selectedClientId) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No client selected"
          message="Select a client to continue."
          testId="dashboard-empty-layout-client"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/clients")}>
          Select Client
        </button>
      </div>
    );
  }

  if (!selectedPropertyId) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No property selected"
          message="Select a property to continue."
          testId="dashboard-empty-layout-property"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/properties")}>
          Select Property
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          {layoutData.title}
        </h1>
        <p className="mt-2 text-sm text-slate-600">{layoutData.subtitle}</p>
      </div>
      {!localStorage.getItem("myv_selected_property") && (
        <FlowGuidanceBanner
          eventKey="layout-missing-property"
          title={flowGuidanceData.layout.missingProperty.title}
          description={flowGuidanceData.layout.missingProperty.description}
          meta={{ page: "layout", missing: "property" }}
        />
      )}
      <div className="grid gap-4 md:grid-cols-[0.9fr_1.1fr]">
        <div className="card card-hover p-6">
          <p className="text-sm font-semibold text-slate-900">
            {layoutData.upload.title}
          </p>
          <p className="mt-2 text-sm text-slate-600">
            {layoutData.upload.description}
          </p>
          <label className="mt-4 block text-xs text-slate-500">
            {layoutData.uploadInputLabel}
            <input
              type="file"
              className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              onChange={() =>
                logActivity("layout_uploaded", {
                  label: layoutData.upload.primaryLabel,
                })
              }
            />
          </label>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              className="btn btn-primary"
              onClick={() =>
                logActivity("layout_upload", {
                  label: layoutData.upload.primaryLabel,
                })
              }
            >
              {layoutData.upload.primaryLabel}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() =>
                logActivity("layout_replace", {
                  label: layoutData.upload.secondaryLabel,
                })
              }
            >
              {layoutData.upload.secondaryLabel}
            </button>
          </div>
          <button
            className="btn btn-primary mt-4 w-full"
            onClick={() =>
              logActivity("layout_continue", {
                label: layoutData.upload.continueLabel,
              })
            }
          >
            {layoutData.upload.continueLabel}
          </button>
        </div>

        <div className="card card-hover p-6">
          <p className="text-sm font-semibold text-slate-900">
            {layoutData.preview.title}
          </p>
          <p className="mt-2 text-sm text-slate-600">
            {layoutData.preview.description}
          </p>
          <div className="mt-4">
            <VastuCompassPlaceholder />
          </div>
          <div className="mt-4">
            <p className="text-sm font-semibold text-slate-900">
              {layoutData.savedLayouts.title}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {layoutData.savedLayouts.selectLabel}
            </p>
            <div className="mt-3 grid gap-3">
              {loadingLayouts
                ? Array.from({ length: 2 }).map((_, index) => (
                    <div key={`layout-skeleton-${index}`} className="card p-3">
                      <div className="h-3 w-24 animate-pulse rounded bg-slate-200" />
                      <div className="mt-2 h-2 w-full animate-pulse rounded bg-slate-100" />
                    </div>
                  ))
                : layouts.map((layout, index) => (
                    <button
                      key={`layout-${index}`}
                      className={`card p-3 text-left transition ${
                        activeLayoutIndex === index
                          ? "border-amber-500 text-slate-900 shadow-md"
                          : "text-slate-600 hover:border-amber-300"
                      }`}
                      onClick={() => {
                        setActiveLayoutIndex(index);
                        logActivity("layout_previewed", { index });
                      }}
                    >
                      <p className="text-sm font-semibold text-slate-900">
                        Layout {index + 1}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {typeof layout === "string" ? layout : "Layout data"}
                      </p>
                    </button>
                  ))}
            </div>
            {!loadingLayouts && layouts.length === 0 && (
              <div className="mt-4">
                <EmptyState
                  title="No layouts saved"
                  message="Save a layout to see it here."
                  testId="dashboard-empty-layouts"
                />
              </div>
            )}
            {layoutsError && (
              <div className="mt-4">
                <ErrorState error={layoutsError} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
